import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getBankIndex } from '../services/dataService';
import { BankIndex, BankCategory } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { useCountry } from '../context/CountryContext';

const BankCard: React.FC<{ bank: BankIndex }> = ({ bank }) => (
    <Link to={`/bank-charges/${bank.slug}`} className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 ease-in-out transform hover:-translate-y-1 border border-gray-200 group">
        <div>
            <h3 className="font-semibold text-gray-800 group-hover:text-primary transition-colors">{bank.name}</h3>
            <p className="text-sm text-gray-500">{bank.category}</p>
        </div>
        <svg className="w-5 h-5 text-gray-400 ml-auto group-hover:text-primary transition-colors" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    </Link>
);

const BankChargesPage: React.FC = () => {
    const { country } = useCountry();
    const [banks, setBanks] = useState<BankIndex[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');

    useEffect(() => {
        setLoading(true);
        getBankIndex(country).then(data => {
            setBanks(data);
            setLoading(false);
        });
    }, [country]);

    const categories = useMemo(() => ['All', ...Object.values(BankCategory)], []);

    const filteredBanks = useMemo(() => {
        return banks.filter(bank => {
            const matchesSearch = bank.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'All' || bank.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [banks, searchTerm, selectedCategory]);

    return (
        <div className="bg-background py-8 sm:py-12">
            <div className="container mx-auto px-4 sm:px-6">
                <header className="text-center mb-8">
                    <h1 className="text-2xl sm:text-4xl font-extrabold font-poppins tracking-tight text-secondary sm:text-5xl">Bank Charges Directory</h1>
                    <p className="mt-3 text-sm sm:text-lg text-neutral-light">Find and compare charges for all major banks.</p>
                </header>
                
                {/* Search and Filter Section - NOT Sticky to ensure it scrolls up */}
                <div className="max-w-4xl mx-auto mb-8">
                    {/* Search Bar */}
                    <div className="relative shadow-sm rounded-lg mb-6">
                         <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
                         </div>
                         <input
                             type="search"
                             className="block w-full py-3 pl-12 pr-4 text-gray-900 border border-gray-200 rounded-lg bg-white placeholder-gray-500 focus:ring-primary focus:border-primary text-base"
                             placeholder="Search for a bank..."
                             value={searchTerm}
                             onChange={(e) => setSearchTerm(e.target.value)}
                             aria-label="Search banks"
                         />
                    </div>

                    {/* Category Filters */}
                    <div className="flex overflow-x-auto pb-2 gap-2 no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                                    selectedCategory === category
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? <LoadingSpinner /> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredBanks.map(bank => (
                            <BankCard key={bank.id} bank={bank} />
                        ))}
                    </div>
                )}
                
                {filteredBanks.length === 0 && !loading && (
                    <div className="text-center py-10 px-6 bg-white rounded-lg shadow-sm border border-gray-200">
                        <h2 className="text-xl font-medium text-gray-800">No Banks Found</h2>
                        <p className="text-gray-500 mt-2">
                            {searchTerm 
                                ? `Your search for "${searchTerm}" did not match any banks.` 
                                : `No banks found in the "${selectedCategory}" category.`}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BankChargesPage;